import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Code2, Check, Copy } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SiteFooter from '@/components/SiteFooter';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const PROD = 'https://howbusyismyairport.com';

const HUBS = [
  ['ATL', 'Atlanta'], ['LAX', 'Los Angeles'], ['ORD', 'Chicago'], ['DFW', 'Dallas'],
  ['DEN', 'Denver'], ['JFK', 'New York'], ['SFO', 'San Francisco'], ['LAS', 'Las Vegas'],
  ['SEA', 'Seattle'], ['MIA', 'Miami'], ['MCO', 'Orlando'], ['EWR', 'Newark'],
  ['BOS', 'Boston'], ['CLT', 'Charlotte'], ['PHX', 'Phoenix'], ['IAH', 'Houston'],
];

function embedSnippet(code) {
  const lc = code.toLowerCase();
  return `<iframe src="${PROD}/api/widget/${lc}" width="280" height="100" style="border:0" scrolling="no" title="${code} airport crowd level — HowBusyIsMyAirport.com"></iframe>
<div style="font-size:11px"><a href="${PROD}/airports/${lc}">Live ${code} crowd levels &amp; best times to fly — HowBusyIsMyAirport.com</a></div>`;
}

export default function WidgetPage() {
  const [code, setCode] = useState('ATL');
  const [copied, setCopied] = useState(false);
  const snippet = embedSnippet(code);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="widget-page">
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <span className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Predictor
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 mb-2 flex items-center gap-2">
              <Code2 className="w-4 h-4" /> Free Embed · No signup
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Airport Crowd Widget
            </h1>
            <p className="text-lg text-slate-600">
              Add a live "how busy is this airport right now" badge to your blog, travel guide, or
              hotel site. It updates itself every 30 minutes, loads in a lightweight iframe, and is
              free to use with the attribution link included in the snippet.
            </p>
          </div>

          <Card className="border-slate-200 bg-white mb-6">
            <CardContent className="p-5 sm:p-6">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                Choose an airport
              </label>
              <select
                data-testid="widget-airport-select"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full sm:w-72 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white mb-6"
              >
                {HUBS.map(([c, city]) => (
                  <option key={c} value={c}>{c} — {city}</option>
                ))}
              </select>

              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Live preview</p>
              <iframe
                data-testid="widget-preview"
                key={code}
                src={`${BACKEND}/api/widget/${code.toLowerCase()}`}
                width="280"
                height="100"
                style={{ border: 0 }}
                scrolling="no"
                title={`${code} airport crowd level`}
                className="mb-6"
              />

              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Embed code</p>
              <pre
                data-testid="widget-embed-code"
                className="bg-slate-900 text-slate-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all mb-3"
              >{snippet}</pre>
              <button
                data-testid="widget-copy-btn"
                onClick={copy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                {copied ? <><Check className="w-4 h-4 text-emerald-400" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy embed code</>}
              </button>
            </CardContent>
          </Card>

          <section className="space-y-4 text-slate-700 leading-relaxed text-sm">
            <h2 className="text-xl font-semibold text-slate-900">Usage & terms</h2>
            <p>
              The widget is free for any website. We ask that you keep the attribution link under the
              badge — it's how readers find the full{' '}
              <Link to="/airports" className="text-blue-600 underline">airport guides</Link> behind
              the number. The badge shows the current crowd level from the same model that powers our{' '}
              <Link to="/" className="text-blue-600 underline">live predictor</Link>, refreshed every
              30 minutes. Need a different airport (we track 331), a custom size, or a white-label
              version? <Link to="/contact" className="text-blue-600 underline">Get in touch</Link>.
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
